// src/pages/Profile/Profile.jsx
import React, { useState, useEffect } from 'react';
import PageTitle from '../../components/SEO/PageTitle';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import CandidateInfo from './components/CandidateInfo';
import CandidateDesires from './components/CandidateDesires';
import CandidateExperience from './components/CandidateExperience';
import DocumentUpload from './components/DocumentUpload';
import ProfileProgress from './components/ProfileProgress';
import ProfileView from './components/ProfileView';
import ProfileForm from './components/ProfileForm';

// Check if profile is complete
const isProfileComplete = (profile) => {
    if (!profile) return false;
    
    // Section 1: Candidate Info
    if (!profile.firstName?.trim()) return false;
    if (!profile.lastName?.trim()) return false;
    if (!profile.phoneNumber?.trim()) return false;
    if (!profile.city?.trim()) return false;
    if (!profile.country?.trim()) return false;
    if (!profile.gender) return false;
    if (!profile.dateOfBirth) return false;
    
    // ID Number check for South Africans
    if (profile.citizenship === 'South African') {
        if (!profile.idNumber?.trim()) return false;
        if (!/^\d{13}$/.test(profile.idNumber)) return false;
    }
    
    // Section 2: Candidate Desires
    if (!profile.lookingFor?.trim()) return false;
    if (!profile.preferredJobLocation?.trim()) return false;
    if (!profile.desiredSalary?.trim()) return false;
    
    // Section 3: Candidate Experience
    if (!profile.professionalSummary?.trim()) return false;
    if (profile.skills?.length < 3) return false;
    if (profile.workExperience?.length === 0 && profile.education?.length === 0) return false;
    
    return true;
};

// Get completion percentage
const getCompletionPercentage = (profile, userData) => {
    if (!profile) return 0;
    
    const firstName = profile.firstName || userData?.firstName || '';
    const lastName = profile.lastName || userData?.lastName || '';
    
    const fields = [
        { value: firstName, label: 'First Name' },
        { value: lastName, label: 'Last Name' },
        { value: profile.phoneNumber, label: 'Phone Number' },
        { value: profile.city, label: 'City' },
        { value: profile.country, label: 'Country' },
        { value: profile.gender, label: 'Gender' },
        { value: profile.dateOfBirth, label: 'Date of Birth' },
        { value: profile.lookingFor, label: 'What are you looking for?' },
        { value: profile.preferredJobLocation, label: 'Preferred Job Location' },
        { value: profile.desiredSalary, label: 'Desired Salary' },
        { value: profile.professionalSummary, label: 'Professional Summary' }
    ];
    
    let completed = 0;
    let total = fields.length;
    
    fields.forEach(field => {
        if (field.value?.trim()) completed++;
    });
    
    // Skills (at least 3)
    if (profile.skills?.length >= 3) completed++;
    total++;
    
    // Experience or Education
    if (profile.workExperience?.length > 0 || profile.education?.length > 0) completed++;
    total++;
    
    // ID Number (if South African)
    if (profile.citizenship === 'South African') {
        total++;
        if (profile.idNumber?.trim() && /^\d{13}$/.test(profile.idNumber)) completed++;
    }
    
    return Math.round((completed / total) * 100);
};

// Get missing fields
const getMissingFields = (profile, userData) => {
    if (!profile) return [];
    const missing = [];
    
    const firstName = profile.firstName || userData?.firstName || '';
    const lastName = profile.lastName || userData?.lastName || '';
    
    if (!firstName?.trim()) missing.push('First Name');
    if (!lastName?.trim()) missing.push('Last Name');
    if (!profile.phoneNumber?.trim()) missing.push('Phone Number');
    if (!profile.city?.trim()) missing.push('City');
    if (!profile.country?.trim()) missing.push('Country');
    if (!profile.gender) missing.push('Gender');
    if (!profile.dateOfBirth) missing.push('Date of Birth');
    
    if (profile.citizenship === 'South African') {
        if (!profile.idNumber?.trim()) missing.push('ID Number (South African)');
        else if (!/^\d{13}$/.test(profile.idNumber)) missing.push('Valid ID Number (13 digits)');
    }
    
    if (!profile.lookingFor?.trim()) missing.push('What are you looking for?');
    if (!profile.preferredJobLocation?.trim()) missing.push('Preferred Job Location');
    if (!profile.desiredSalary?.trim()) missing.push('Desired Salary');
    if (!profile.professionalSummary?.trim()) missing.push('Professional Summary');
    if (profile.skills?.length < 3) missing.push(`Skills (need ${3 - (profile.skills?.length || 0)} more)`);
    if (profile.workExperience?.length === 0 && profile.education?.length === 0) {
        missing.push('Work Experience OR Education (at least one)');
    }
    
    return missing;
};

const Profile = () => {
    const { currentUser } = useAuth();
    const [profile, setProfile] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        if (currentUser) {
            loadProfile();
        }
    }, [currentUser]);

    const loadProfile = async () => {
        try {
            // 1. Get user data from `users` collection
            const userDocRef = doc(db, 'users', currentUser.uid);
            const userDocSnap = await getDoc(userDocRef);
            const userDocData = userDocSnap.exists() ? userDocSnap.data() : {};
            setUserData(userDocData);

            // 2. Get profile data from `candidates` collection
            const candidateDocRef = doc(db, 'candidates', currentUser.uid);
            const candidateDocSnap = await getDoc(candidateDocRef);
            const candidateData = candidateDocSnap.exists() ? candidateDocSnap.data() : {};

            // 3. Merge them
            const mergedProfile = {
                // From users collection
                firstName: userDocData.firstName || '',
                lastName: userDocData.lastName || '',
                email: currentUser.email || '',
                
                // From candidates collection (or empty defaults)
                phoneNumber: candidateData.phoneNumber || '',
                gender: candidateData.gender || '',
                currentJobTitle: candidateData.currentJobTitle || '',
                streetAddress: candidateData.streetAddress || '',
                postcode: candidateData.postcode || '',
                city: candidateData.city || '',
                country: candidateData.country || 'South Africa',
                dateOfBirth: candidateData.dateOfBirth || '',
                hasDisability: candidateData.hasDisability || 'Prefer not to say',
                ethnicity: candidateData.ethnicity || '',
                citizenship: candidateData.citizenship || 'South African',
                idNumber: candidateData.idNumber || '',
                lookingFor: candidateData.lookingFor || '',
                preferredJobLocation: candidateData.preferredJobLocation || '',
                willingToRelocate: candidateData.willingToRelocate || false,
                desiredSalary: candidateData.desiredSalary || '',
                professionalSummary: candidateData.professionalSummary || '',
                workExperience: candidateData.workExperience || [],
                currentlyWorking: candidateData.currentlyWorking || false,
                currentSalary: candidateData.currentSalary || '',
                noticePeriod: candidateData.noticePeriod || '',
                education: candidateData.education || [],
                hasDriversLicence: candidateData.hasDriversLicence || false,
                skills: candidateData.skills || [],
                languages: candidateData.languages || [],
                linkedin: candidateData.linkedin || '',
                portfolio: candidateData.portfolio || '',
                // 🔥 FIX: Add certifications and references
                certifications: candidateData.certifications || [],
                references: candidateData.references || [],
                profileCompleted: candidateData.profileCompleted || false,
                profileCreatedAt: candidateData.profileCreatedAt || new Date().toISOString(),
                profileUpdatedAt: candidateData.profileUpdatedAt || new Date().toISOString(),
            };

            setProfile(mergedProfile);
            
            // Check if profile is complete and update if needed
            const complete = isProfileComplete(mergedProfile);
            if (complete !== mergedProfile.profileCompleted) {
                await updateDoc(doc(db, 'candidates', currentUser.uid), {
                    profileCompleted: complete
                });
                mergedProfile.profileCompleted = complete;
            }
            
        } catch (error) {
            console.error("Error loading profile:", error);
        } finally {
            setLoading(false);
        }
    };

    const saveProfile = async (updatedData) => {
        try {
            // 1. Update users collection (for name)
            const userDocRef = doc(db, 'users', currentUser.uid);
            await updateDoc(userDocRef, {
                firstName: updatedData.firstName,
                lastName: updatedData.lastName,
            });

            // 2. Update candidates collection (for everything else)
            const candidateDocRef = doc(db, 'candidates', currentUser.uid);
            const saveData = { ...updatedData };
            delete saveData.firstName;
            delete saveData.lastName;
            delete saveData.email;
            saveData.profileUpdatedAt = new Date().toISOString();
            
            // 🔥 FIX: Ensure certifications and references are included
            saveData.certifications = updatedData.certifications || [];
            saveData.references = updatedData.references || [];
            
            // Check if profile is complete
            const complete = isProfileComplete(updatedData);
            saveData.profileCompleted = complete;
            
            await setDoc(candidateDocRef, saveData, { merge: true });

            // 3. Update local state
            setProfile(prev => ({ ...prev, ...updatedData, profileCompleted: complete }));
            
            return { success: true };
        } catch (error) {
            console.error("Error saving:", error);
            return { success: false, error: error.message };
        }
    };

    const handleSave = async (updatedData) => {
        const result = await saveProfile(updatedData);
        if (result.success) {
            setIsEditing(false);
            alert('Profile saved successfully!');
        } else {
            alert('Error saving profile: ' + result.error);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        loadProfile(); // Reload to discard changes
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <i className="fas fa-spinner fa-spin text-4xl text-gradpath-green"></i>
            </div>
        );
    }

    if (!currentUser) {
        return (
            <div className="container mx-auto px-4 py-16 text-center">
                <div className="bg-white rounded-xl p-8 max-w-md mx-auto">
                    <i className="fas fa-lock text-5xl text-gray-400 mb-4"></i>
                    <h2 className="text-2xl font-bold text-gray-700">Login Required</h2>
                    <p className="text-gray-500">Please login to view your profile</p>
                </div>
            </div>
        );
    }

    const completion = getCompletionPercentage(profile, userData);
    const isComplete = isProfileComplete(profile);
    const missingFields = getMissingFields(profile, userData);

    return (
        <div className="container mx-auto px-4 py-8 max-w-4xl">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <PageTitle 
                title="My Profile" 
                description="Manage your GradPath profile, update your CV, and track job applications"
            />
                {/* Header with Progress */}
                <div className="bg-gradient-to-r from-gradpath-green to-gradpath-dark p-6 text-white">
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-2xl font-bold">My Profile</h1>
                            <p className="text-white/80 text-sm mt-1">
                                {profile.firstName} {profile.lastName} • {profile.email}
                            </p>
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold">{completion}%</div>
                            <div className="text-xs text-white/70">Profile Complete</div>
                            {isComplete && (
                                <div className="text-xs text-green-300 mt-1">
                                    <i className="fas fa-check-circle mr-1"></i> Ready to apply
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-2 mt-3">
                        <div 
                            className={`h-full rounded-full transition-all duration-500 ${
                                completion === 100 ? 'bg-green-400' : 'bg-white'
                            }`}
                            style={{ width: `${completion}%` }}
                        ></div>
                    </div>
                </div>

                {/* Missing Fields Warning */}
                {!isComplete && missingFields.length > 0 && (
                    <div className="bg-yellow-50 border-b border-yellow-200 p-4">
                        <div className="flex items-start gap-3">
                            <i className="fas fa-exclamation-triangle text-yellow-600 mt-0.5"></i>
                            <div>
                                <h4 className="font-medium text-yellow-800">Profile Incomplete</h4>
                                <p className="text-sm text-yellow-700 mt-1">Complete these fields to reach 100%:</p>
                                <ul className="list-disc list-inside text-sm text-yellow-700 mt-1 ml-2">
                                    {missingFields.map((field, index) => (
                                        <li key={index}>{field}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {/* Profile Content */}
                <div className="p-6">
                    {isEditing ? (
                        <ProfileForm 
                            profile={profile}
                            onSave={handleSave}
                            onCancel={handleCancel}
                        />
                    ) : (
                        <ProfileView 
                            profile={profile}
                            onEdit={handleEdit}
                            completion={completion}
                            isComplete={isComplete}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default Profile;